import { type ReactNode, type ButtonHTMLAttributes, forwardRef } from "react";
import { cn } from "@/lib/cn";

type Variant = "primary" | "secondary" | "ghost" | "outline" | "danger" | "subtle";
type Size = "xs" | "sm" | "md" | "lg" | "icon";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

const variants: Record<Variant, string> = {
  primary:
    "bg-[var(--color-primary)] text-[var(--color-void)] font-semibold hover:bg-[var(--color-primary-bright)] active:scale-[0.98] shadow-[0_8px_24px_-12px_rgba(94,234,212,0.6)]",
  secondary:
    "bg-[var(--color-secondary)] text-[var(--color-void)] font-semibold hover:bg-[var(--color-secondary-bright)] active:scale-[0.98]",
  ghost:
    "text-[var(--color-ink-soft)] hover:text-[var(--color-ink)] hover:bg-[var(--color-surface-2)]",
  outline:
    "border border-[var(--color-border-bright)] text-[var(--color-ink)] hover:bg-[var(--color-surface-2)] hover:border-[var(--color-primary)]",
  danger:
    "bg-[var(--color-danger)] text-white font-semibold hover:brightness-110 active:scale-[0.98]",
  subtle:
    "bg-[var(--color-surface-2)] text-[var(--color-ink-soft)] hover:bg-[var(--color-surface-3)] hover:text-[var(--color-ink)]",
};

const sizes: Record<Size, string> = {
  xs: "h-7 px-2.5 text-[11px] gap-1.5 rounded-md",
  sm: "h-8 px-3 text-xs gap-1.5 rounded-lg",
  md: "h-10 px-4 text-sm gap-2 rounded-lg",
  lg: "h-12 px-6 text-base gap-2.5 rounded-xl",
  icon: "h-9 w-9 rounded-lg",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = "primary", size = "md", leftIcon, rightIcon, className, children, ...rest }, ref) => (
    <button
      ref={ref}
      className={cn(
        "inline-flex items-center justify-center font-medium transition-all duration-150 select-none",
        "disabled:opacity-40 disabled:pointer-events-none whitespace-nowrap",
        variants[variant],
        sizes[size],
        className,
      )}
      {...rest}
    >
      {leftIcon}
      {children}
      {rightIcon}
    </button>
  ),
);
Button.displayName = "Button";
